import { Box, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";

import theme from "@/theme";

import { BottomSheet } from "../BottomSheet";
import { CustomButton } from "../Button";
import { Chevron } from "../Chevron";

import { RADAR_SCORE_LABELS, RadarScoreKey } from "./CompatibilityRadarChart";

// 軸ラベル（物件種別・年収など）だけでは何と何を比べているのか伝わらないため、
// 「あなたの◯◯ × プランナーの◯◯」の対比で説明する。
// スコアの算出そのもの（AgentCompatibilityRadarScorer の 1〜5 点の配点）は説明せず、
// 「何を見ているか」に絞る。配点を書くとユーザーが点数の交渉・逆算を始めるうえ、
// API 側の配点変更のたびに文言が嘘になるため。
const AXIS_DESCRIPTIONS: Record<RadarScoreKey, string> = {
  property_type: "あなたが興味のある物件タイプ（区分・一棟など）を、どれだけ扱っているか",
  annual_income: "あなたの年収が、多く担当している年収帯にどれだけ近いか",
  financial_asset: "あなたの金融資産が、対応できる資産帯にどれだけ近いか",
  investment_stage: "あなたの投資経験（はじめて／経験あり）を、どれだけ重点的にサポートしているか",
  area: "あなたのお住まいのエリアを、どれだけカバーしているか",
};

const useStyles = makeStyles(() => ({
  infoLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: theme.palette.semantic["text/link"],
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.3,
    padding: "10px 0",
    alignSelf: "flex-start",
  },
  infoIcon: { width: 16, height: 16, flexShrink: 0 },
  helpRow: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: 8,
    textAlign: "left",
  },
  helpTexts: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 0 },
  helpTitle: { fontSize: 12, fontWeight: 700, lineHeight: 1.3, color: theme.palette.semantic["status/info"] },
  helpDescription: { fontSize: 12, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
  compactHelpRow: {
    height: 44,
    "& $helpTitle": { fontSize: 14, fontWeight: 500 },
    "& img": { display: "block", flexShrink: 0 },
  },
  lead: { fontSize: 14, lineHeight: 1.5, color: theme.palette.semantic["text/primary"] },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
  },
  bulletRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    "& + &": {
      marginTop: theme.spacing(1),
    },
  },
  legendSwatch: {
    width: 14,
    height: 14,
    borderRadius: 4,
    flexShrink: 0,
    marginTop: 2,
  },
  yourSwatch: {
    backgroundColor: theme.palette.semantic["chart/baseline"],
  },
  theirSwatch: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    backgroundColor: "transparent",
  },
  bulletDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 6,
    marginLeft: 5,
    marginRight: 5,
    backgroundColor: theme.palette.text.secondary,
  },
  bulletText: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  axisRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    "& + &": {
      marginTop: theme.spacing(1),
    },
  },
  axisLabel: {
    flex: "0 0 72px",
    borderRadius: 4,
    backgroundColor: theme.palette.semantic["surface/base"],
    padding: theme.spacing(0.5),
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.semantic["text/primary"],
    textAlign: "center",
  },
  axisDescription: {
    flex: "1 1 auto",
    minWidth: 0,
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  note: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(3),
    "& + &": { marginTop: 4 },
  },
}));

interface CompatibilityRadarInfoModalProps {
  onClose: () => void;
}

export const CompatibilityRadarInfoModal: React.FC<CompatibilityRadarInfoModalProps> = ({ onClose }) => {
  const classes = useStyles();

  return (
    <BottomSheet
      open
      title="相性グラフの見かた"
      zIndex={theme.zIndex.modal + 2}
      onOpen={() => undefined}
      onClose={onClose}
      footer={
        <CustomButton tone="text" buttonSize="L" fullWidth onClick={onClose}>
          閉じる
        </CustomButton>
      }
    >
      <Typography className={classes.lead}>
        あなたが登録した5つの条件と、このプランナーが得意とする条件がどれくらい噛み合っているかを、項目ごとに5段階で表しています。
      </Typography>

      <Typography className={classes.sectionTitle}>グラフの読み方</Typography>
      <Box className={classes.bulletRow}>
        <Box className={`${classes.legendSwatch} ${classes.theirSwatch}`} />
        <Typography className={classes.bulletText}>
          このプランナーとの相性（青い線）：外側にあるほど、あなたの条件と噛み合っています
        </Typography>
      </Box>
      <Box className={classes.bulletRow}>
        <Box className={`${classes.legendSwatch} ${classes.yourSwatch}`} />
        <Typography className={classes.bulletText}>
          相性基準ライン（グレーの面）：青い線がグレーの外に出ている項目ほど、あなたに合っています
        </Typography>
      </Box>
      <Box className={classes.bulletRow}>
        <Box className={classes.bulletDot} />
        <Typography className={classes.bulletText}>5つの項目の平均が、相性スコア（5点満点）です</Typography>
      </Box>

      <Typography className={classes.sectionTitle}>5つの項目</Typography>
      {RADAR_SCORE_LABELS.map(({ key, label }) => (
        <Box className={classes.axisRow} key={key}>
          <Typography className={classes.axisLabel}>{label}</Typography>
          <Typography className={classes.axisDescription}>{AXIS_DESCRIPTIONS[key]}</Typography>
        </Box>
      ))}

      <Typography className={classes.note}>
        ※
        スコアはプランナーの優劣ではなく、あなたとの相性です。低い項目があっても「良くないプランナー」という意味ではありません。
      </Typography>
      <Typography className={classes.note}>
        ※ 判定にはマイページに登録した情報を使っています。情報を更新すると相性も変わります。
      </Typography>
    </BottomSheet>
  );
};

/** 一覧では説明リンク、グラフの直下では説明行を表示する。 */
export const CompatibilityRadarInfoButton: React.FC<{ variant?: "link" | "row" | "compact-row"; surface?: "base" | "card" }> = ({
  variant = "link",
  surface = "base",
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = React.useState(false);
  const isRow = variant !== "link";
  const isCompact = variant === "compact-row";

  return (
    <Box display="contents" onClick={(event) => event.stopPropagation()}>
      <ButtonBase
        className={isRow ? `${classes.helpRow} ${isCompact ? classes.compactHelpRow : ""}` : classes.infoLink}
        // ButtonBase の生成順に左右されず、指定した地の色を維持する。
        style={isRow ? { backgroundColor: theme.palette.semantic[surface === "card" ? "surface/card" : "surface/base"] } : undefined}
        disableRipple
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(true);
        }}
        aria-haspopup="dialog"
        aria-label={isRow ? "相性グラフの見かた" : "相性について"}
      >
        <img src={isCompact ? "/icons/planner_onboarding/help.svg" : isRow ? "/icons/ds-v2-compatibility-help.svg" : "/icons/ds-v2-compatibility-info.svg"} alt="" aria-hidden="true" className={classes.infoIcon} />
        {isRow ? (
          <>
            <Box component="span" className={classes.helpTexts}>
              <Typography component="span" className={classes.helpTitle}>
                相性グラフの見かた
              </Typography>
              {!isCompact && <Typography component="span" className={classes.helpDescription}>
                5つの項目と、グラフの読み方
              </Typography>}
            </Box>
            {isCompact ? <img src="/icons/planner_onboarding/chevron_primary.svg" alt="" /> : <Chevron direction="forward" />}
          </>
        ) : (
          "相性について"
        )}
      </ButtonBase>
      {isOpen && <CompatibilityRadarInfoModal onClose={() => setIsOpen(false)} />}
    </Box>
  );
};
